import React, { useCallback } from 'react';
import { Layout } from '../../../queries/ats/layout/types';
import { Provider, ThirdPartyProvider } from '../../../queries/ats/me/providers/types';
import { tracker } from '../../../tracker';
import { ThirdPartyServiceOpenedEvent } from '../../../tracker/events/third-party-service-opened';
import { usePageContext } from '../../../views/ats-pages/page/page-context';
import { useSectionContext } from '../../../views/ats-pages/section/section-context';
import { ThirdPartySourceIndicator } from '../ats-asset/source-indicator/source-indicator';
import { ThirdPartyDeepLink } from '../ats-deep-links/third-party-deep-link';
import { AtsItemSharedProps } from './ats-item.interfaces';

interface ThirdPartyExternalLinkProps extends AtsItemSharedProps {
  item: Layout.SectionItemLink;
  provider: ThirdPartyProvider;
  children: React.ReactNode;
  pattern?: Layout.Pattern;
}

export const ThirdPartyExternalLink = React.forwardRef<HTMLAnchorElement, ThirdPartyExternalLinkProps>(
  ({ item, provider, pattern, children, onClick, ...rest }, ref) => {
    const page = usePageContext('ThirdPartyExternalLink');
    const section = useSectionContext('ThirdPartyExternalLink');

    const track = useCallback(() => {
      tracker.track(new ThirdPartyServiceOpenedEvent(item, provider, page.slug, section.slug, pattern));
    }, [item, provider, page, section, pattern]);

    return (
      <ThirdPartyDeepLink
        type={provider.params.popupTextType}
        link={getProviderUrl(item.params.url, provider)}
        providerIdentifier={provider.id}
        providerName={provider.name}
        onClick={onClick}
        track={track}
      >
        <a {...rest} href={getLink(provider, item)} onClick={track} ref={ref}>
          {children}
          {pattern !== 'streaming-service' && <ThirdPartySourceIndicator provider={provider} />}
        </a>
      </ThirdPartyDeepLink>
    );
  },
);

ThirdPartyExternalLink.displayName = 'ThirdPartyExternalLink';

function getLink(provider: Provider, item: Layout.SectionItemLink) {
  switch (provider.type) {
    case 'thirdParty':
      return item.params.url;
    case 'svod':
      return item.params.url;
    default:
      return item.params.url;
  }
}

function getProviderUrl(url: string, provider: ThirdPartyProvider) {
  try {
    const itemUrl = new URL(url);
    return itemUrl.toString();
  } catch (error) {
    if (provider.params.deeplinkUrlBase === undefined) {
      return provider.params.homeUrl;
    }

    const providerUrl = new URL(`${provider.params.deeplinkUrlBase}`, provider.params.deeplinkUrlBase);
    const search =
      provider.params.deeplinkQueryParams !== undefined
        ? new URLSearchParams(provider.params.deeplinkQueryParams)
        : undefined;

    if (search) {
      search.forEach((value, key) => providerUrl.searchParams.set(key, value));
    }

    return providerUrl.toString();
  }
}
